// Устойчивый object URL для одних и тех же байтов.
//
// Проблема, которую он решает. Обложки и иллюстрации читаются cache-first: код
// достаёт Blob из офлайн-кэша и делает `URL.createObjectURL(blob)`. Адрес
// создавался ЗАНОВО на каждый монтаж компонента и отзывался при размонтаже,
// поэтому при каждом входе на экран браузер видел новый ресурс и декодировал
// картинку с нуля. Снаружи это выглядит как мерцание: плитки появляются не
// сразу, хотя байты давно лежат на устройстве.
//
// Замер на Android 2026-08-27, экран «Курсы»: 23 картинки, 16 из них blob;
// после ухода на главную и возврата — те же изображения, но ДРУГИЕ адреса
// (`92af1233…` → `dbe543fa…`). Сетевых загрузок при этом ноль: расход целиком
// на повторное декодирование.
//
// Что даёт устойчивый адрес: одинаковый URL между монтажами → у браузера
// срабатывает собственный кэш декодированного изображения → картинка рисуется
// в том же кадре, без промежуточного пустого места. Плюс появляется
// СИНХРОННЫЙ путь: если адрес уже известен, компоненту не нужно уходить в
// асинхронное чтение кэша и показывать заглушку.
//
// Почему адреса не отзываются сразу. Отзыв освобождает байты, но убивает и
// кэш декодирования — ровно то, ради чего всё затевалось. Поэтому живём
// ограниченным набором и отзываем только вытесненное.

/** Сколько адресов держим живыми. Порядок вставки в Map = порядок вытеснения. */
const MAX_ENTRIES = 60;

const urls = new Map<string, string>();

/**
 * Уже известный адрес для ключа, без чтения кэша и без асинхронности.
 *
 * Синхронность здесь — не оптимизация, а способ убрать мерцание: компонент,
 * получивший адрес на первом же рендере, не проходит через состояние «ещё не
 * знаю, что показать».
 */
export function peekStableObjectUrl(key: string): string | null {
	const url = urls.get(key);
	if (!url) return null;
	// Обновляем позицию: недавно использованное вытесняется последним.
	urls.delete(key);
	urls.set(key, url);
	return url;
}

/**
 * Запомнить байты под ключом и вернуть устойчивый адрес.
 *
 * Повторный вызов с тем же ключом возвращает ПРЕЖНИЙ адрес и НЕ создаёт новый:
 * иначе смысл теряется — у браузера снова окажется незнакомый ресурс.
 */
export function rememberStableObjectUrl(key: string, blob: Blob): string {
	const existing = peekStableObjectUrl(key);
	if (existing) return existing;

	const url = URL.createObjectURL(blob);
	urls.set(key, url);

	while (urls.size > MAX_ENTRIES) {
		const oldestKey = urls.keys().next().value;
		if (oldestKey === undefined) break;
		const oldestUrl = urls.get(oldestKey);
		urls.delete(oldestKey);
		// Вытесненное отзываем — иначе байты держались бы в памяти вечно.
		if (oldestUrl) URL.revokeObjectURL(oldestUrl);
	}

	return url;
}

/**
 * Забыть ключ (например, картинку удалили или перегенерировали).
 *
 * Нужен именно явный сброс: иначе после перегенерации обложки экран показывал
 * бы старые байты столько, сколько ключ живёт в наборе.
 */
export function forgetStableObjectUrl(key: string): void {
	const url = urls.get(key);
	if (!url) return;
	urls.delete(key);
	URL.revokeObjectURL(url);
}

/** Только для тестов: размер набора. */
export function stableObjectUrlCount(): number {
	return urls.size;
}
